/**
 * Horarios de pedido — Portal Cliente Menuee
 *
 * Construye slots seleccionables desde la sucursal seleccionada:
 * - sucursales.horarios (JSON v2 por día, v1 turnos)
 * - horario_atencion / horario_reparto (texto legacy)
 *
 * Intervalo alineado con la app previa (30 min).
 */

export const INTERVALO_MINUTOS_HORARIO_PEDIDO = 30;

export const ZONA_HORARIA_SUCURSAL = "America/Santiago";

const HORARIOS_VERSION_POR_DIA = 2;

const DIAS_SEMANA = [
  { key: "lunes" },
  { key: "martes" },
  { key: "miercoles" },
  { key: "jueves" },
  { key: "viernes" },
  { key: "sabado" },
  { key: "domingo" },
];

const DIA_KEYS_ORDEN = DIAS_SEMANA.map(({ key }) => key);

const DIA_KEY_POR_WEEKDAY_EN = {
  Sun: "domingo",
  Mon: "lunes",
  Tue: "martes",
  Wed: "miercoles",
  Thu: "jueves",
  Fri: "viernes",
  Sat: "sabado",
};

const DIA_NOMBRE_ES = {
  lunes: "lunes",
  martes: "martes",
  miercoles: "miércoles",
  jueves: "jueves",
  viernes: "viernes",
  sabado: "sábado",
  domingo: "domingo",
};

const TIME_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;

function normalizarHorariosDesdeDb(raw) {
  if (raw == null) return null;
  if (typeof raw === "object" && !Array.isArray(raw)) return raw;
  if (typeof raw === "string") {
    try {
      const v = JSON.parse(raw);
      if (v && typeof v === "object" && !Array.isArray(v)) return v;
    } catch {
      return null;
    }
  }
  return null;
}

function esHorarioPorDiaValido(obj) {
  if (!obj || typeof obj !== "object") return false;
  if (obj.version !== HORARIOS_VERSION_POR_DIA) return false;
  if (!obj.porDia || typeof obj.porDia !== "object" || Array.isArray(obj.porDia)) {
    return false;
  }
  return DIAS_SEMANA.every(({ key }) => Array.isArray(obj.porDia[key]));
}

function tiempoValido(s) {
  return typeof s === "string" && TIME_RE.test(s.trim());
}

/**
 * @param {string} hora
 * @returns {number|null}
 */
export function minutosDesdeHora(hora) {
  if (!tiempoValido(hora)) return null;
  const [h, m] = String(hora)
    .trim()
    .split(":")
    .map((x) => Number(x));
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
  return h * 60 + m;
}

/**
 * @param {number} minutos
 * @returns {string}
 */
export function formatearHora(minutos) {
  const h = Math.floor(minutos / 60);
  const m = minutos % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

function diaAnterior(diaKey) {
  const idx = DIA_KEYS_ORDEN.indexOf(diaKey);
  if (idx < 0) return DIA_KEYS_ORDEN[DIA_KEYS_ORDEN.length - 1];
  return DIA_KEYS_ORDEN[(idx + DIA_KEYS_ORDEN.length - 1) % DIA_KEYS_ORDEN.length];
}

function diaDesplazado(diaKey, cantidad) {
  const idx = DIA_KEYS_ORDEN.indexOf(diaKey);
  if (idx < 0) return diaKey;
  const len = DIA_KEYS_ORDEN.length;
  return DIA_KEYS_ORDEN[(idx + cantidad + len * 8) % len];
}

/** fin=00:00 cierra a medianoche. fin < inicio (ej. 02:00 < 19:00) sigue de madrugada. */
function bloqueCruzaAlDiaSiguiente(ini, fin) {
  if (ini == null || fin == null) return false;
  if (fin === 0) return false;
  return fin < ini;
}

/**
 * Reloj de Chile (America/Santiago). Nunca usa now() de Supabase ni UTC del servidor.
 * Algunos motores devuelven hour=24 a medianoche; se normaliza a 0.
 *
 * @param {Date} [fecha]
 * @param {string} [timeZone]
 * @returns {{ diaKey: string|null, minutos: number }}
 */
export function instanteEnChile(
  fecha = new Date(),
  timeZone = ZONA_HORARIA_SUCURSAL
) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    weekday: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    hourCycle: "h23",
  }).formatToParts(fecha instanceof Date ? fecha : new Date(fecha));
  const get = (type) => parts.find((p) => p.type === type)?.value;
  let hour = Number.parseInt(get("hour"), 10);
  const minute = Number.parseInt(get("minute"), 10);
  if (hour === 24) hour = 0;
  const weekday = String(get("weekday") || "")
    .replace(/[^A-Za-z]/g, "")
    .slice(0, 3);
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) {
    return { diaKey: null, minutos: 0 };
  }
  return {
    diaKey: DIA_KEY_POR_WEEKDAY_EN[weekday] || null,
    minutos: hour * 60 + minute,
  };
}

function diaKeyHoy(timeZone = ZONA_HORARIA_SUCURSAL) {
  return instanteEnChile(new Date(), timeZone).diaKey;
}

/**
 * Minutos desde medianoche en horario de Chile.
 *
 * @param {string} [timeZone]
 * @param {Date} [fecha]
 * @returns {number}
 */
export function minutosAhoraEnZona(
  timeZone = ZONA_HORARIA_SUCURSAL,
  fecha = new Date()
) {
  return instanteEnChile(fecha, timeZone).minutos;
}

/** Turno diario repetido (v1 / texto). fin=00:00 cierra a medianoche; fin < inicio cruza. */
function instanteEnBloque(minutos, ini, fin) {
  if (ini == null || fin == null) return false;
  if (fin === 0) return minutos >= ini;
  if (fin > ini) return minutos >= ini && minutos < fin;
  return minutos >= ini || minutos < fin;
}

/**
 * Bloque de un día v2. La madrugada de un cruce pertenece al día que abrió
 * (ayer 19:00–02:00), no al día calendario de hoy.
 */
function instanteEnBloqueMismoDia(minutos, ini, fin) {
  if (ini == null || fin == null) return false;
  if (fin === 0) return minutos >= ini;
  if (fin > ini) return minutos >= ini && minutos < fin;
  return minutos >= ini;
}

function estaAbiertaEnChile(sucursal, modoEntrega, diaKey, minutos) {
  const h = normalizarHorariosDesdeDb(sucursal?.horarios);
  if (h && esHorarioPorDiaValido(h) && diaKey) {
    for (const b of h.porDia[diaKey] || []) {
      if (
        instanteEnBloqueMismoDia(
          minutos,
          minutosDesdeHora(b.inicio),
          minutosDesdeHora(b.fin)
        )
      ) {
        return true;
      }
    }
    const prev = diaAnterior(diaKey);
    for (const b of h.porDia[prev] || []) {
      const ini = minutosDesdeHora(b.inicio);
      const fin = minutosDesdeHora(b.fin);
      if (bloqueCruzaAlDiaSiguiente(ini, fin) && minutos < fin) return true;
    }
    return false;
  }

  const rama = esModoDelivery(modoEntrega) ? "reparto" : "atencion";
  const turnosV1 =
    (h && Array.isArray(h[rama]?.turnos) && h[rama].turnos) ||
    (h && Array.isArray(h.atencion?.turnos) && h.atencion.turnos) ||
    [];
  if (turnosV1.length) {
    return turnosV1.some((b) =>
      instanteEnBloque(minutos, minutosDesdeHora(b.inicio), minutosDesdeHora(b.fin))
    );
  }

  const rango = parseHorarioRangoTexto(sucursal, modoEntrega);
  if (!rango) return false;
  return instanteEnBloque(
    minutos,
    minutosDesdeHora(rango.inicio),
    minutosDesdeHora(rango.fin)
  );
}

/** Prefijo 00:00, 00:30… hasta el primer hueco (continuación del turno nocturno). */
function prefijoMadrugadaPorCruce(ordenados) {
  if (!ordenados.length || minutosDesdeHora(ordenados[0]) !== 0) {
    return [];
  }
  const prefix = [ordenados[0]];
  for (let i = 1; i < ordenados.length; i += 1) {
    const prev = minutosDesdeHora(ordenados[i - 1]);
    const cur = minutosDesdeHora(ordenados[i]);
    if (prev == null || cur == null) break;
    if (cur !== prev + INTERVALO_MINUTOS_HORARIO_PEDIDO) break;
    prefix.push(ordenados[i]);
  }
  return prefix;
}

/**
 * Antes de la primera hora configurada se muestran todos los slots.
 * Si el turno cruza medianoche, de noche la madrugada siguiente no es “ya pasó”:
 * a las 23:44 siguen valiendo 00:00, 00:30, etc.
 *
 * @param {string[]} slots
 * @param {number} [ahoraMinutos]
 * @returns {string[]}
 */
export function filtrarSlotsSegunHoraActual(
  slots,
  ahoraMinutos = minutosAhoraEnZona()
) {
  if (!Array.isArray(slots) || slots.length === 0) {
    return [];
  }

  const ordenados = [...slots].sort(
    (a, b) => (minutosDesdeHora(a) ?? 0) - (minutosDesdeHora(b) ?? 0)
  );
  const minutos = ordenados
    .map((slot) => minutosDesdeHora(slot))
    .filter((m) => m != null);
  if (minutos.length === 0) {
    return ordenados;
  }

  const primer = minutos[0];
  const madrugada = prefijoMadrugadaPorCruce(ordenados);
  const tieneNoche = minutos.some((m) => m >= 18 * 60);
  const cruzaMedianoche = madrugada.length > 0 && tieneNoche;

  if (ahoraMinutos < primer && !(cruzaMedianoche && ahoraMinutos >= 18 * 60)) {
    return ordenados;
  }

  const desdeAhora = ordenados.filter((slot) => {
    const min = minutosDesdeHora(slot);
    return min != null && min >= ahoraMinutos;
  });

  if (cruzaMedianoche && ahoraMinutos >= 18 * 60) {
    const combinados = [];
    const visto = new Set();
    for (const slot of [...desdeAhora, ...madrugada]) {
      if (visto.has(slot)) continue;
      visto.add(slot);
      combinados.push(slot);
    }
    return combinados;
  }

  return desdeAhora;
}

function bloquesCandidatosHoy(horariosNorm, ahoraMinutos = minutosAhoraEnZona(), diaKey = diaKeyHoy()) {
  if (!diaKey) return [];

  const out = [...(horariosNorm.porDia[diaKey] || [])];
  const prev = diaAnterior(diaKey);

  for (const b of horariosNorm.porDia[prev] || []) {
    const ini = minutosDesdeHora(b.inicio);
    const fin = minutosDesdeHora(b.fin);
    if (ini == null || fin == null) continue;
    if (!bloqueCruzaAlDiaSiguiente(ini, fin)) continue;
    if (ahoraMinutos < fin) {
      out.push({ ...b, inicio: "00:00", fin: b.fin });
    }
  }

  return out;
}

function rangosMinutosDeBloque(inicioStr, finStr) {
  const inicio = minutosDesdeHora(inicioStr);
  const fin = minutosDesdeHora(finStr);
  if (inicio == null || fin == null || inicio === fin) return [];
  if (fin === 0) return [[inicio, 24 * 60]];
  if (fin > inicio) return [[inicio, fin]];
  return [
    [inicio, 24 * 60],
    [0, fin],
  ];
}

/**
 * @param {Array<{ inicio?: string, fin?: string }>} bloques
 * @param {number} intervaloMinutos
 * @returns {string[]}
 */
function generarSlotsDesdeBloques(bloques, intervaloMinutos) {
  const visto = new Set();
  const salida = [];

  for (const bloque of bloques || []) {
    const rangos = rangosMinutosDeBloque(bloque?.inicio, bloque?.fin);
    for (const [inicio, fin] of rangos) {
      for (let min = inicio; min < fin; min += intervaloMinutos) {
        const slot = formatearHora(min);
        if (!visto.has(slot)) {
          visto.add(slot);
          salida.push(slot);
        }
      }
    }
  }

  return salida.sort((a, b) => minutosDesdeHora(a) - minutosDesdeHora(b));
}

function esModoDelivery(modoEntrega) {
  return modoEntrega === "domicilio" || modoEntrega === "delivery";
}

/**
 * @param {Object|null|undefined} horariosRaw
 * @param {string|null|undefined} modoEntrega
 * @returns {string[]}
 */
function construirDesdeHorariosJson(
  horariosRaw,
  modoEntrega,
  ahoraMinutos = minutosAhoraEnZona(),
  diaKey = diaKeyHoy()
) {
  const h = normalizarHorariosDesdeDb(horariosRaw);
  if (!h) return [];

  if (esHorarioPorDiaValido(h)) {
    const bloques = bloquesCandidatosHoy(h, ahoraMinutos, diaKey);
    return generarSlotsDesdeBloques(bloques, INTERVALO_MINUTOS_HORARIO_PEDIDO);
  }

  const rama = esModoDelivery(modoEntrega) ? "reparto" : "atencion";
  const turnos = h[rama]?.turnos;
  if (Array.isArray(turnos) && turnos.length) {
    return generarSlotsDesdeBloques(turnos, INTERVALO_MINUTOS_HORARIO_PEDIDO);
  }

  const turnosAtencion = h.atencion?.turnos;
  if (Array.isArray(turnosAtencion) && turnosAtencion.length) {
    return generarSlotsDesdeBloques(turnosAtencion, INTERVALO_MINUTOS_HORARIO_PEDIDO);
  }

  return [];
}

function construirSlotsCrudos(
  sucursal,
  modoEntrega = null,
  ahoraMinutos = minutosAhoraEnZona(),
  diaKey = diaKeyHoy()
) {
  if (!sucursal) return [];

  if (Array.isArray(sucursal.slots_horarios) && sucursal.slots_horarios.length) {
    return [...sucursal.slots_horarios];
  }
  if (
    Array.isArray(sucursal.horarios_disponibles) &&
    sucursal.horarios_disponibles.length
  ) {
    return [...sucursal.horarios_disponibles];
  }

  const desdeJson = construirDesdeHorariosJson(
    sucursal.horarios,
    modoEntrega,
    ahoraMinutos,
    diaKey
  );
  if (desdeJson.length) {
    return desdeJson;
  }

  const rango = parseHorarioRangoTexto(sucursal, modoEntrega);
  if (!rango) return [];

  return generarSlotsEnRango(
    rango.inicio,
    rango.fin,
    INTERVALO_MINUTOS_HORARIO_PEDIDO
  );
}

function horaAperturaDeBloques(bloques, ahoraMinutos = null) {
  let mejor = null;
  for (const b of bloques || []) {
    const ini = minutosDesdeHora(b?.inicio);
    const fin = minutosDesdeHora(b?.fin);
    if (ini == null || fin == null) continue;
    if (ahoraMinutos != null && ini <= ahoraMinutos) continue;
    if (mejor == null || ini < mejor) mejor = ini;
  }
  return mejor != null ? formatearHora(mejor) : null;
}

/**
 * Próxima apertura en calendario Chile (no usa now() de Supabase).
 *
 * @param {Object|null|undefined} sucursal
 * @param {string|null|undefined} modoEntrega
 * @returns {{ diasHasta: number, diaKey: string, hora: string }|null}
 */
function resolverProximaApertura(
  sucursal,
  modoEntrega = null,
  ahoraMinutos = minutosAhoraEnZona(),
  diaKeyHoyParam = diaKeyHoy()
) {
  const h = normalizarHorariosDesdeDb(sucursal?.horarios);
  const hoy = diaKeyHoyParam;

  if (h && esHorarioPorDiaValido(h) && hoy) {
    const horaHoy = horaAperturaDeBloques(h.porDia[hoy] || [], ahoraMinutos);
    if (horaHoy) {
      return { diasHasta: 0, diaKey: hoy, hora: horaHoy };
    }
    for (let i = 1; i <= 7; i += 1) {
      const diaKey = diaDesplazado(hoy, i);
      const hora = horaAperturaDeBloques(h.porDia[diaKey] || []);
      if (hora) {
        return { diasHasta: i, diaKey, hora };
      }
    }
    return null;
  }

  const slotsRepetidos = construirSlotsCrudos(
    sucursal,
    modoEntrega,
    ahoraMinutos,
    hoy
  );
  const hora = slotsRepetidos[0] || null;
  if (!hora) {
    return null;
  }
  return { diasHasta: 1, diaKey: hoy ? diaDesplazado(hoy, 1) : "lunes", hora };
}

function mensajeCierrePorApertura(proxima) {
  if (!proxima?.hora) {
    return "Estamos cerrados. Podrás pedir el próximo día de atención.";
  }
  if (proxima.diasHasta === 0) {
    return `Estamos cerrados. Podrás pedir hoy a partir de las ${proxima.hora}.`;
  }
  if (proxima.diasHasta === 1) {
    return `Estamos cerrados. Podrás pedir mañana a partir de las ${proxima.hora}.`;
  }
  const nombre = DIA_NOMBRE_ES[proxima.diaKey] || proxima.diaKey;
  return `Estamos cerrados. Podrás pedir el ${nombre} a partir de las ${proxima.hora}.`;
}

/**
 * @param {Object|null|undefined} sucursal
 * @param {string|null|undefined} modoEntrega
 * @returns {{ inicio: string, fin: string }|null}
 */
export function parseHorarioRangoTexto(sucursal, modoEntrega = null) {
  if (!sucursal) return null;

  const apertura = sucursal.hora_apertura || sucursal.apertura || null;
  const cierre = sucursal.hora_cierre || sucursal.cierre || null;
  if (apertura && cierre) {
    return { inicio: apertura, fin: cierre };
  }

  const textos = esModoDelivery(modoEntrega)
    ? [
        sucursal.horario_reparto,
        sucursal.horario_atencion,
        sucursal.horario_pedidos,
      ]
    : [
        sucursal.horario_atencion,
        sucursal.horario_reparto,
        sucursal.horario_pedidos,
      ];

  for (const texto of textos) {
    const match = String(texto || "").match(/(\d{1,2}:\d{2}).*?(\d{1,2}:\d{2})/);
    if (match) {
      const pad = (s) => {
        const [hh, mm] = s.split(":");
        return `${hh.padStart(2, "0")}:${mm}`;
      };
      return { inicio: pad(match[1]), fin: pad(match[2]) };
    }
  }

  return null;
}

function generarSlotsEnRango(inicio, fin, intervaloMinutos) {
  const rangos = rangosMinutosDeBloque(inicio, fin);
  if (!rangos.length) return [];

  const salida = [];
  const visto = new Set();
  for (const [inicioMin, finMin] of rangos) {
    for (let min = inicioMin; min < finMin; min += intervaloMinutos) {
      const slot = formatearHora(min);
      if (visto.has(slot)) continue;
      visto.add(slot);
      salida.push(slot);
    }
  }
  return salida;
}

/**
 * Slots HH:MM para el selector de horario del pedido.
 * Usa hora de Chile (America/Santiago), nunca now() de Supabase.
 *
 * @param {Object|null|undefined} sucursal
 * @param {{ modoEntrega?: string|null, ahoraMinutos?: number, diaKey?: string|null, fecha?: Date }} [opciones]
 * @returns {string[]}
 */
export function construirHorariosDisponibles(
  sucursal,
  { modoEntrega = null, ahoraMinutos, diaKey, fecha } = {}
) {
  return resolverEstadoHorarioPedido(sucursal, {
    modoEntrega,
    ahoraMinutos,
    diaKey,
    fecha,
  }).slots;
}

/**
 * @typedef {Object} EstadoHorarioPedido
 * @property {boolean} cerrado
 * @property {string[]} slots
 * @property {string|null} mensajeCierre
 * @property {{ diasHasta: number, diaKey: string, hora: string }|null} proximaApertura
 */

/**
 * Estado de pedidos según horario de Chile (America/Santiago).
 * Abierto si el reloj de Chile está dentro de un bloque (aunque no quede un slot de 30 min).
 * No usa now() de Supabase.
 *
 * @param {Object|null|undefined} sucursal
 * @param {{ modoEntrega?: string|null, ahoraMinutos?: number, diaKey?: string|null, fecha?: Date }} [opciones]
 * @returns {EstadoHorarioPedido}
 */
export function resolverEstadoHorarioPedido(
  sucursal,
  { modoEntrega = null, ahoraMinutos, diaKey, fecha } = {}
) {
  const chile = instanteEnChile(fecha ?? new Date());
  const ahora = ahoraMinutos ?? chile.minutos;
  const dia = diaKey ?? chile.diaKey;
  const crudos = construirSlotsCrudos(sucursal, modoEntrega, ahora, dia);
  const slots = filtrarSlotsSegunHoraActual(crudos, ahora);
  const abiertaAhora = estaAbiertaEnChile(sucursal, modoEntrega, dia, ahora);

  if (slots.length > 0 || abiertaAhora) {
    const slotsFinal =
      slots.length > 0 ? slots : [formatearHora(ahora)];
    return {
      cerrado: false,
      slots: slotsFinal,
      mensajeCierre: null,
      proximaApertura: null,
    };
  }

  const proximaApertura = resolverProximaApertura(sucursal, modoEntrega, ahora, dia);
  return {
    cerrado: true,
    slots: [],
    mensajeCierre: mensajeCierrePorApertura(proximaApertura),
    proximaApertura,
  };
}

/**
 * @param {string|null|undefined} horario
 * @param {string[]} slots
 * @returns {boolean}
 */
export function horarioValido(horario, slots) {
  if (!Array.isArray(slots) || slots.length === 0) {
    return false;
  }
  if (!horario) {
    return false;
  }
  return slots.includes(horario);
}
